<template>
  <popup-dialog :visible="visible" :close-button-visible="true">
    <div class="publication-popup">
      <h2 class="publication-popup__title">
        <template v-if="publicationId">{{ t("objectNames.publication") }}{{ t(":") }} {{ publicationId }}</template>
        <template v-else>
          {{ $t("actions.publishModel") }}
        </template>
      </h2>
      
      <form class="publication-popup__form" @submit.prevent="">
        <input-texteditor
          v-model:value="data.description"
          :disabled="!hasPublishingUpgrade"
          :label="$t('labels.description')"
        />

        <details-block
          v-model:open="state.accessConfigOpen"
          class="publication-popup__config-toggle mt-4"
        >
          <template #header>
            <h3 class="mv-2 subtitle publication-popup__config-heading">
              {{ $t("headings.accessSettings") }}
              <limits-badge
                v-if="!props.hasPublishingUpgrade"
                type="canPublish"
                :title="$t('errors.noPublishingUpgrade')"
                class="ml-2"
              />
            </h3>
          </template>

          <div class="publication-popup__config" :class="{ '-disabled': !hasPublishingUpgrade }">
            <div v-if="!hasPublishingUpgrade" class="publication-popup__config__message">
              {{ $t("errors.noPublishingUpgrade") }}

              <div class="flex mt-5">
                <router-link
                  :to="{ name: 'settings-subscriptions' }"
                  class="button button_secondary mi-auto"
                >
                  {{ $t("links.toSubscriptions") }}
                </router-link>
              </div>
            </div>
            
            <div class="publication-popup__iconline">
              <lucide-icon name="clipboard" class="publication-popup__icon" />
              <input-checkbox
                v-model:value="data.canFork"
                :disabled="!hasPublishingUpgrade"
                :label="$t('labels.canBeCopied')"
              />
            </div>
            <div v-if="user?.isSamplemodelAdmin" class="publication-popup__iconline">
              <lucide-icon name="star" class="publication-popup__icon" />
              <input-checkbox
                v-model:value="data.isExampleModel"
                :disabled="!hasPublishingUpgrade"
                :label="$t('labels.isExampleModel')"
              />
            </div>

            <div class="publication-popup__iconline">
              <lucide-icon name="calendar" class="publication-popup__icon" />
              <section>
                <div class="publication-popup__line--times">
                  <div class="publication-popup__line--time">
                    <input-text
                      v-model:value="data.startsAtDate"
                      :disabled="!hasPublishingUpgrade"
                      :label="$t('labels.validFrom')"
                      type="date"
                    />
                    <input-text
                      v-if="state.timeUsed"
                      :disabled="!hasPublishingUpgrade"
                      v-model:value="data.startsAtTime"
                      type="time"
                    />
                  </div>
                  <div class="publication-popup__line--time">
                    <input-text
                      v-model:value="data.endsAtDate"
                      :disabled="!hasPublishingUpgrade"
                      :label="$t('labels.validUntil')"
                      type="date"
                      :error="state.error?.fields?.ends_at"
                    />
                    <input-text
                      v-if="state.timeUsed"
                      v-model:value="data.endsAtTime"
                      :disabled="!hasPublishingUpgrade"
                      type="time"
                    />
                  </div>
                </div>
                <input-checkbox
                  v-model:value="state.timeUsed"
                  :disabled="!hasPublishingUpgrade"
                  class="mt-3"
                  :label="$t('labels.timeOptionsVisible')"
                />
              </section>
            </div>

            <div class="publication-popup__iconline">
              <lucide-icon name="lock" class="publication-popup__icon" />
              <div>
                <div
                  v-if="publication?.hasPassword"
                  class="publication-popup__info mb-2"
                >{{ $t("labels.passwordProtected") }}</div>
                <div class="publication-popup__formline">
                  <input-checkbox
                    v-model:value="state.overwritingPassword"
                    :disabled="!hasPublishingUpgrade"
                    :label="publication?.hasPassword ? $t('actions.overwritePassword') : $t('actions.setPassword')"
                    class="publication-popup__pwcheckbox"
                    @update:value="$nextTick(() => passwordField?.focus?.())"
                  />
                  <input hidden type="text" name="username" :value="publication?.id" autocomplete="off" />
                  <input-password
                    v-show="state.overwritingPassword"
                    :disabled="!hasPublishingUpgrade"
                    id="publication-password-field"
                    ref="passwordField"
                    v-model:value="data.password"
                    name="password"
                    autocomplete="new-password"
                    class="publication-popup__pwinput"
                  />
                </div>
              </div>
            </div>
          </div>
        </details-block>

        <details-block
          v-model:open="state.visibilityConfigOpen"
          class="publication-popup__config-toggle"
        >
          <template #header>
            <h3 class="mv-2 subtitle publication-popup__config-heading">
              {{ $t("headings.visibilitySettings") }}
              <limits-badge
                v-if="!props.hasPublishingUpgrade"
                type="canPublish"
                :title="$t('errors.noPublishingUpgrade')"
                class="ml-2"
              />
            </h3>
          </template>

          <div class="publication-popup__config" :class="{ '-disabled': !hasPublishingUpgrade }">
            <div v-if="!hasPublishingUpgrade" class="publication-popup__config__message">
              {{ $t("errors.noPublishingUpgrade") }}

              <div class="flex mt-5">
                <router-link
                  :to="{ name: 'settings-subscriptions' }"
                  class="button button_secondary mi-auto"
                >
                  {{ $t("links.toSubscriptions") }}
                </router-link>
              </div>
            </div>
            
            <div class="publication-popup__iconline">
              <lucide-icon name="eye" class="publication-popup__icon" />
              <div class="column gap-4">
                <div>
                  <input-checkbox
                    v-model:value="state.areasRestricted"
                    :disabled="!hasPublishingUpgrade"
                  >
                    {{ $t('labels.restrictEditorAreas') }}
                  </input-checkbox>

                  <template v-if="state.areasRestricted">
                    <h4 class="label">{{ $t("labels.accessableAreas") }}</h4>
                    <div class="publication-popup__areas gap-2 mt-2">
                      <label
                        v-for="area in editorAreas"
                        :key="area"
                        class="publication-popup__area"
                        :class="{ '-checked': data.accessableAreas != null && data.accessableAreas.includes(area) }"
                      >
                        <div>{{ editorAreaNames[area] }}</div>
                        <input-checkbox
                          :disabled="!hasPublishingUpgrade"
                          :value="
                            data.accessableAreas != null && data.accessableAreas.includes(area)
                          "
                          @update:value="setAreaAccess(area, $event)"
                        />
                      </label>
                    </div>
                  </template>
                </div>

                <div>
                  <input-dropdown
                    :label="$t('labels.startingArea')"
                    :disabled="!hasPublishingUpgrade"
                    :options="state.areasRestricted ? data.accessableAreas : editorAreas"
                    :value="data.startingArea"
                    v-slot="{ item }"
                    @update:value="data.startingArea = $event"
                  >
                    {{ editorAreaNames[item as EditorAreaName] }}
                  </input-dropdown>
                </div>
              </div>
            </div>

            <div class="publication-popup__iconline">
              <lucide-icon name="eye" class="publication-popup__icon" />
              <div>
                <input-checkbox
                  v-model:value="state.scenesRestricted"
                  :disabled="!hasPublishingUpgrade"
                >
                  {{ $t('labels.restrictModelScenes') }}
                </input-checkbox>
    
                <template v-if="state.scenesRestricted">
                  <div class="publication-popup__scenes column gap-2 mt-2" v-if="modelSceneLoadStatus === 'success'">
                    <label
                      v-for="scene in modelScenes"
                      :key="scene.id"
                      class="publication-popup__scene"
                    >
                      <div>{{ scene.title }}</div>
    
                      <button class="button-icon p-2" type="button" @click.prevent="toggleSceneHidden(scene.id)">
                        <template v-if="data.hiddenScenes.includes(scene.id)">
                          <lucide-icon name="eye-off" />
                        </template>
                        <template v-else>
                          <lucide-icon name="eye" />
                        </template>
                      </button>
                    </label>
                  </div>
                  <div v-else-if="modelSceneLoadStatus === 'pending'" class="publication-popup__info">
                    {{ $t('messages.loadingData') }}
                  </div>
                  <div v-else-if="modelSceneLoadStatus === 'error'" class="message-error">
                    {{ $t('errors.couldNotLoadPublications') }}
                  </div>
                </template>
              </div>
            </div>

            <div class="publication-popup__iconline">
              <lucide-icon name="eye" class="publication-popup__icon" />
              <div>
                <input-checkbox
                  v-model:value="state.boundToStakeholder"
                  :disabled="!hasPublishingUpgrade"
                >
                  {{ $t('actions.linkToStakeholder') }}
                </input-checkbox>
    
                <template v-if="state.boundToStakeholder">
                  <div class="publication-popup__stakeholders column gap-2 mt-2" v-if="stakeholdersLoadStatus === 'success'">
                    <input-radio
                      v-for="stakeholder in stakeholders"
                      :key="stakeholder.id"
                      class="publication-popup__stakeholder"
                      :data="stakeholder.id"
                      v-model:value="data.stakeholderId"
                    >
                      {{ stakeholder.title }}
                    </input-radio>
                  </div>
                  <div v-else-if="stakeholdersLoadStatus === 'pending'" class="publication-popup__info">
                    {{ $t('messages.loadingData') }}
                  </div>
                  <div v-else-if="stakeholdersLoadStatus === 'error'" class="message-error">
                    {{ $t('errors.couldNotLoadStakeholders') }}
                  </div>
                </template>
              </div>
              
            </div>

            <div class="publication-popup__iconline">
              <lucide-icon name="eye" class="publication-popup__icon" />
              <div>
                <input-checkbox
                  v-model:value="data.infoPopupsLocked"
                  :disabled="!hasPublishingUpgrade"
                >
                  {{ $t('labels.restrictInfoPopups') }}
                </input-checkbox>
              </div>
            </div>
          </div>
        </details-block>

        <div v-if="state.error?.message" class="message message-error">
          {{ state.error?.message }}
        </div>

        <button
          v-if="publicationId != null"
          class="button mt-4"
          type="button"
          @click="save()"
        >
          {{ $t("actions.save") }}
        </button>
        <button v-else class="button" type="button" @click="publish()">
          {{ $t("actions.publishModelNow") }}
        </button>
      </form>
    </div>
  </popup-dialog>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import { showError } from "@core/ui/alert/alert";
import { ModelNotReadableError } from "@core/errors/Errors";
import encryptor from "@/helpers/data/Encryptor";

import LucideIcon from "@core/ui/LucideIcon.vue";
import PopupDialog from "@core/ui/PopupDialog.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import InputText from "@core/input/InputText.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import InputPassword from "@core/input/InputPassword.vue";
import InputRadio from "@core/input/RadioButton.vue";
import LimitsBadge from "@core/ui/LimitsBadge.vue";

import InputTexteditor from "@core/input/TextEditor.vue";
import DetailsBlock from "@core/ui/DetailsBlock.vue";

import { useUser } from "@/api/user.store";
import { useModels } from "@/api/models.store";
import type { EditorAreaName } from "@/app/editor/store/editorAreas";
import type { ModelPublication } from "@/api/ModelPublication";

const props = defineProps<{
  visible: boolean;
  modelId: number | string;
  workspaceId: number;
  publicationId?: string;
  hasPublishingUpgrade: boolean;
}>();

const emit = defineEmits<{
  (event: "publish", publication: ModelPublication): void;
  (event: "save", publication: ModelPublication): void;
}>();

const { t } = useI18n();

const passwordField = ref<null | InstanceType<typeof InputPassword>>(null);
const { user } = useUser();
const { currentModel, createModelPublication, updateModelPublication, getModelData } =
  useModels(props);

const now = new Date();
now.setMinutes(now.getMinutes() - now.getTimezoneOffset());

const editorAreas = [
  "model",
  "variables",
  "inspector",
  "simulation",
  "evaluation"
] as const;

const data = reactive({
  canFork: true,
  isExampleModel: false,
  startsAtDate: now.toISOString().slice(0, 10),
  startsAtTime: now.toISOString().slice(11, 16),
  endsAtDate: undefined as undefined | string,
  endsAtTime: "23:59",
  description: "",
  password: "",
  accessableAreas: [
    "inspector",
    "simulation",
    "evaluation"
  ] as EditorAreaName[],
  startingArea: null as EditorAreaName | null,
  hiddenScenes: [] as number[],
  infoPopupsLocked: false,
  stakeholderId: null as number | null
});

const state = reactive({
  accessConfigOpen: props.hasPublishingUpgrade,
  visibilityConfigOpen: props.hasPublishingUpgrade,
  areasRestricted: false,
  error: null as null | (Error & { fields?: Record<string, string> }),
  timeUsed: false,
  overwritingPassword: false,
  scenesRestricted: false,
  boundToStakeholder: false
});

const editorAreaNames = computed(() => {
  return {
    model: t("areas.network"),
    variables: t("areas.variables"),
    inspector: t("areas.analysis"),
    simulation: t("areas.simulation"),
    evaluation: t("areas.evaluation")
  };
});

const publication = computed(() => {
  const publications = currentModel.value?.publications ?? [];
  return publications.find((p) => p.id === props.publicationId);
});

watch(
  () => props.publicationId,
  (value) => {
    state.timeUsed = false;
    state.overwritingPassword = false;
    if (value != null) {
      const pub = publication.value;

      if (pub == null) {
        console.warn("Publication could not be found.");
        return;
      }

      data.description = pub.description;
      data.canFork = pub.canFork;
      data.isExampleModel = pub.isExampleModel;
      data.password = pub?.password ?? "";
      data.startingArea = pub?.startingArea ?? null;

      state.areasRestricted = pub?.accessableAreas != null;
      if (pub.accessableAreas) {
        data.accessableAreas = [...pub.accessableAreas];
      }

      if (pub.startsAt) {
        data.startsAtDate = pub.startsAt.substring(0, 10);
        data.startsAtTime = pub.startsAt.substring(11, 16);
        if (data.startsAtTime != "00:00") {
          state.timeUsed = true;
        }
      }
      if (pub.endsAt) {
        data.endsAtDate = pub.endsAt.substring(0, 10);
        data.endsAtTime = pub.endsAt.substring(11, 16);
        if (data.endsAtTime != "23:59") {
          state.timeUsed = true;
        }
      }

      state.scenesRestricted = pub?.hiddenScenes != null;
      if (pub.hiddenScenes) {
        data.hiddenScenes = [...pub.hiddenScenes];
      }

      data.infoPopupsLocked = pub.infoPopupsLocked ?? false;
      data.stakeholderId = pub.stakeholderId ?? null;
      if (data.stakeholderId != null) {
        state.boundToStakeholder = true;
      }
    }
  }, { immediate: true }
);

watch(() => state.areasRestricted, (restricted) => {
  if (restricted) {
    if (data.startingArea != null && !data.accessableAreas.includes(data.startingArea)) {
      if (data.accessableAreas.length > 0) {
        data.startingArea = data.accessableAreas[0];
      } else {
        data.startingArea = null;
      }
    }
  }
});
    

function setAreaAccess(areaId: EditorAreaName, shouldHaveAccess: boolean) {
  if (data.accessableAreas == null) data.accessableAreas = [];
  const index = data.accessableAreas.indexOf(areaId);
  if (shouldHaveAccess && index < 0) {
    data.accessableAreas.push(areaId);
  } else if (!shouldHaveAccess && index > -1) {
    data.accessableAreas.splice(index, 1);
  }
  if (data.startingArea == areaId && !shouldHaveAccess) {
    data.startingArea = null;
  }
}

async function publish() {
  state.error = null;
  const publicationData = formToPublicationData();
  try {
    if (
      publicationData.accessableAreas &&
      publicationData.accessableAreas.length == 0
    ) {
      throw new Error(t("errors.publicationNeedsAreas"));
    }

    if (typeof props.modelId == "string") { 
      return showError(t("errors.publishUnsynchedModel"));
    }

    const publication = await createModelPublication(
      props.modelId,
      publicationData
    );

    emit("publish", publication);
  } catch (error) {
    updateErrorMessage(error as Error);
  }
}

async function save() {
  state.error = null;
  
  try {
    const publicationData = formToPublicationData();
  
    const publicationId = publicationData.id;
    if (publicationId == null) {
      return publish();
    }

    if (
      publicationData.accessableAreas &&
      publicationData.accessableAreas.length == 0
    ) {
      throw new Error(t("errors.publicationNeedsAreas"));
    }

    const updatedPublication = await updateModelPublication(
      props.modelId,
      { ...publicationData, id: publicationId }
    );
    emit("save", updatedPublication);
  } catch (error) {
    updateErrorMessage(error as Error);
  }
}

function updateErrorMessage(error: Error) {
  state.error = error;
  console.log("error", { ...error });
}

function formToPublicationData() {
  const publicationData: {
    canFork: boolean;
    isExampleModel: boolean;
    description: string;
    password?: string | null;
    id?: string;
    startsAt?: string | null;
    endsAt?: string | null;
    accessableAreas: EditorAreaName[] | null;
    startingArea?: EditorAreaName | null;
    hiddenScenes: number[] | null;
    infoPopupsLocked: boolean;
    stakeholderId?: number | null;
  } = {
    canFork: data.canFork,
    isExampleModel: data.isExampleModel,
    description: data.description,
    accessableAreas: null,
    hiddenScenes: null,
    infoPopupsLocked: data.infoPopupsLocked,
    stakeholderId: data.stakeholderId ?? null
  };

  if (state.overwritingPassword) {
    publicationData.password = data.password ? data.password : null;
  }

  if (props.publicationId) {
    publicationData.id = props.publicationId;
  }

  if (data.startsAtDate) {
    const time = state.timeUsed ? data.startsAtTime || "00:00" : "00:00";
    const timestring = `${data.startsAtDate}T${time}`;
    const startDate = new Date(timestring);
    publicationData.startsAt = startDate.toISOString();
  } else {
    publicationData.startsAt = null;
  }
  if (data.endsAtDate) {
    const time = state.timeUsed ? data.endsAtTime || "23:59" : "23:59";
    const timestring = `${data.endsAtDate}T${time}`;
    const endDate = new Date(timestring);
    publicationData.endsAt = endDate.toISOString();
  } else {
    publicationData.endsAt = null;
  }

  if (state.areasRestricted) {
    publicationData.accessableAreas = data.accessableAreas;
  } else {
    publicationData.accessableAreas = null;
  }

  publicationData.startingArea = data.startingArea;

  if (state.scenesRestricted) {
    publicationData.hiddenScenes = data.hiddenScenes;
    if (publicationData.hiddenScenes.length === 0) {
      publicationData.hiddenScenes = null;
    } else if (publicationData.hiddenScenes.length === modelScenes.value.length) {
      // The user cannot hide ALL scenes - what should be shown then?
      throw new Error(t("errors.cannotHideAllScenes"));
    }
  } else {
    publicationData.hiddenScenes = null;
  }

  if (state.boundToStakeholder) {
    publicationData.stakeholderId = data.stakeholderId;
  } else {
    publicationData.stakeholderId = null;
  }

  publicationData.infoPopupsLocked = data.infoPopupsLocked;

  return publicationData;
}

// =============================
// MODEL DATA

// For some settings, we need access to the inner model structure, so we load it on demand.

const modelData = ref<any>(null);
const modelDataLoadStatus = ref<"pending" | "success" | "error">("pending");

async function loadModelData() {
  if (modelDataLoadStatus.value === "success") return;
  modelDataLoadStatus.value = "pending";
  try {
    const modelDataSet = await getModelData(props.workspaceId, props.modelId, {}, true);
    if (!modelDataSet || !modelDataSet.data) {
      throw new Error("Model data could not be loaded.");
    }
    // Decrypt that data
    encryptor.reset();
    modelData.value = await encryptor.decryptAskingForPassword(
      modelDataSet.data
    );
    if (!modelData.value) {
      throw new ModelNotReadableError();
    }
    modelDataLoadStatus.value = "success";
  } catch (error) {
    showError(error instanceof Error ? error : new Error(String(error)));
    modelDataLoadStatus.value = "error";
  }
}

// =============================
// SCENES

const modelScenes = ref<{ title: string; id: number }[]>([]);
const modelSceneLoadStatus = ref<"pending" | "success" | "error">("pending");
watch(() => state.scenesRestricted, async (restricted) => {
  if (!restricted) return;
  if (modelSceneLoadStatus.value === "success") return;
  modelSceneLoadStatus.value = "pending";
  try {
    const scenes = await loadModelScenes();
    modelScenes.value = scenes;
    modelSceneLoadStatus.value = "success";
  } catch (error) {
    showError(error instanceof Error ? error : new Error(String(error)));
    modelSceneLoadStatus.value = "error";
  }
}, { immediate: true });

async function loadModelScenes(): Promise<{ title: string; id: number }[]> {
  const defaultScenes = [
    { id: 0, title: t("objectNames.scene") + " 1" }
  ];
  await loadModelData();
  if (!modelData.value) {
    throw new ModelNotReadableError();
  }
  const scenes = modelData.value.scenes;
  if (!scenes || Object.keys(scenes).length === 0) {
    return defaultScenes;
  }

  return Object.values(scenes.items as Record<number, { title: string; id: number }>).map((scene) => ({
    id: scene.id,
    title: scene.title || `${t("objectNames.scene")} ${scene.id + 1}`
  }));
}

function toggleSceneHidden(sceneId: number) {
  const index = data.hiddenScenes.indexOf(sceneId);
  if (index > -1) {
    data.hiddenScenes.splice(index, 1);
  } else {
    data.hiddenScenes.push(sceneId);
  }
}

// =============================
// STAKEHOLDERS

const stakeholders = ref<{ id: number; title: string }[]>([]);
const stakeholdersLoadStatus = ref<"pending" | "success" | "error">("pending");
watch(() => state.boundToStakeholder, async (bound) => {
  if (!bound) {
    data.stakeholderId = null;
    return;
  }
  if (stakeholdersLoadStatus.value === "success") return;
  stakeholdersLoadStatus.value = "pending";
  try {
    const stakeholdersData = await loadStakeholders();
    stakeholders.value = stakeholdersData;
    stakeholdersLoadStatus.value = "success";
  } catch (error) {
    showError(error instanceof Error ? error : new Error(String(error)));
    stakeholdersLoadStatus.value = "error";
  }
}, { immediate: true });

async function loadStakeholders(): Promise<{ id: number; title: string }[]> {
  await loadModelData();
  if (!modelData.value) {
    throw new ModelNotReadableError();
  }
  const stakeholders = modelData.value.stakeholders;
  if (!stakeholders || Object.keys(stakeholders).length === 0) {
    return [];
  }

  return Object.values(stakeholders.items as Record<number, { title: string; id: number }>).map((stakeholder) => ({
    id: stakeholder.id,
    title: stakeholder.title || `${t("objectNames.stakeholder")} ${stakeholder.id + 1}`
  }));
}
</script>

<style lang="scss">
.publication-popup {
  padding: 2rem;

  &__title {
    margin-top: 0;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 0;

    &line {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }

  &__iconline {
    display: grid;
    grid-template-columns: min-content 1fr;
    gap: var(--gap, 1rem);
  }

  &__line--times {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(20rem, 1fr));
    gap: 1rem;
    @media screen and (max-width: 350px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }
  &__line--time {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(8.5rem, 1fr));
    align-items: flex-end;
    justify-content: flex-start;
    gap: 0.5rem;
    @media screen and (max-width: 350px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }
  &__pwcheckbox {
    flex-shrink: 0;
  }
  &__pwinput {
    margin-top: -5px;
    margin-bottom: -5px;
  }

  &__areas {
    display: grid;
    grid-template-columns: repeat(5, auto);
    grid-template-rows: auto;
    position: relative;
    background-color: rgba(var(--tint-contrast), 2%);
    border-radius: 0.25rem;
    padding: 0.75rem;
    box-shadow: inset 0 1px 2px -1px var(--color-shadow-1);
  }
  &__area {
    display: flex;
    flex-direction: column;
    border-radius: 0.25rem;
    gap: 0.5rem;
    font-weight: 500;
    color: var(--color-text-medium);
    text-align: center;
    user-select: none;
    padding: 0.75rem 0.5rem;
    &.-checked {
      color: var(--color-primary);
      background-color: var(--color-primary-light);
    }

    .checkbox {
      justify-content: center;
    }

    // input {
    //   position: absolute;
    //   opacity: 0;
    //   left: 0;
    //   top: 0;
    //   pointer-events: none;
    // }
  }

  &__scenes {
    display: grid;
    grid-template-columns: max-content min-content;
    width: max-content;
    max-width: 100%;
    column-gap: 0.5rem;
    row-gap: 0;
    padding: 0.5rem 1rem;
    background-color: rgba(var(--tint-contrast), 2%);
    border-radius: 0.25rem;
  }
  &__scene {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: 1fr min-content;
    grid-template-columns: subgrid;
    align-items: center;
    gap: 0.5rem;
    user-select: none;
  }

  &__info {
    padding: 0.5rem;
    border: 1px solid var(--color-border);
    width: fit-content;
    border-radius: 0.5rem;
    background-color: var(--color-background-shallow);
    font-size: var(--font-size-label);
  }

  &__icon {
    color: var(--color-text-light);
    font-size: 1.5em;
  }

  &__config {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 0.5rem 0;
    padding-bottom: 1.5rem;
    
    &.-disabled > * {
      pointer-events: none;
      opacity: 0.5;
    }
    &__message {
      opacity: 1 !important;
      pointer-events: auto !important;
      padding: 1rem;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: var(--color-background-deep);
      border-radius: 0.5rem;
      z-index: 1;
      box-shadow: 0 0 1rem var(--color-shadow-1);
    }
  }

  &__config-toggle {
    font-size: 1rem;
    color: var(--color-text-medium);
    transition: color 200ms ease-out;
    .lucide-icon {
      font-size: 1.25em;
    }
    &:hover {
      color: var(--color-text);
    }
  }

  &__config-heading {
    position: relative;
    padding-right: 2.5em;
  }
}
</style>
